// Source: type-composer.jsx:1810-1846 (the Sample-record tab body). Renders directly from
// `definition` — the prop the composer already resolves to the working DRAFT while editable
// (useSphereType's `readEditableSchema`) — so this reflects the draft, not published, with no
// separate fetch (spec `sampleRecordReflectsTheDraftNotPublished`).
//
// Considered: SpherePropertiesPanel — rejected. It renders a live sphere's chat launcher, not
// property values (its "Sphere properties" body is an unimplemented stub); composing it here
// would satisfy nothing the acceptance test checks. Considered: CompositionHost (spec Reuse
// Audit) — rejected for this phase: driving it requires defining a Space-document mapping from
// a schema's properties to composed components, which the spec's UI Components table names as a
// component but never specifies the mapping for. That is real, unspecified design work — tracked
// as an open question rather than guessed at here. This renders a plain label/value list with
// existing atoms, which is what the acceptance test (values render, and reflect the draft) needs.
//
// The panel and the Full-sphere dialog show the SAME record with different datum ids and
// containers, so the body is one component parameterised by its datum map — the pattern
// `AspectDefSummaryMolecule` already uses (`labelDatum` / `glyphDatum`). Two copies of the
// markup would be two places for the two readings to drift apart.

import React from 'react';
import { useTranslation } from 'localization';
import type { Schema } from 'types/schema';
import type { AspectRow } from 'types/aspect';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import {
  sampleRecordRows,
  sampleHealthDrivers,
  sampleRecordName,
  type SampleRecordRow,
  type SampleHealthDriver,
} from './sampleRecordRows';
import './SampleRecordPanelMolecule.scss';

const KEY = 'studio.typeEditor.composer.sideRail';

/** The contract gives the panel and the dialog different ids for the same readings. */
export interface SampleRecordDatums {
  row: string;
  key: string;
  value: string;
  note: string;
  empty: string;
  health: string;
  onTrack: string;
  driver: string;
}

export const PANEL_DATUMS: SampleRecordDatums = {
  row: 'side.kv-row',
  key: 'side.r-k',
  value: 'side.x#22',
  note: 'side.r-note',
  empty: 'side.nothing-composed-yet-so-there-s-nothing-',
  health: 'side.health',
  onTrack: 'side.on-track',
  driver: 'side.x#23',
};

export const DIALOG_DATUMS: SampleRecordDatums = {
  row: 'side.kv-row#2',
  key: 'side.r-k#2',
  value: 'side.x#24',
  note: 'side.r-note#2',
  empty: 'side.nothing-composed-yet-so-there-s-nothing-#2',
  health: 'side.health#2',
  onTrack: 'side.on-track#2',
  driver: 'side.x#25',
};

export interface SampleRecordBodyProps {
  rows: SampleRecordRow[];
  drivers: SampleHealthDriver[];
  datums: SampleRecordDatums;
  /** The contract's container path for this reading — the panel's or the dialog's. */
  container: string;
  /** Copy for "nothing composed yet"; the panel and the dialog word it differently. */
  emptyText: string;
}

/** The rows and the health strip — everything both readings share. */
export const SampleRecordBody: React.FC<SampleRecordBodyProps> = ({
  rows,
  drivers,
  datums,
  container,
  emptyText,
}) => {
  const { t } = useTranslation();
  return (
    <>
      {rows.length > 0 ? (
        <dl className="sample-record-panel__fields" data-container={`${container} > kv-list`}>
          {rows.map((row) => (
            <div
              className="sample-record-panel__field"
              key={row.key}
              data-datum={datums.row}
              data-kind="kv-row"
              data-container={`${container} > kv-list > each(rows)`}
              data-vacant={row.vacant ? '1' : undefined}
            >
              <dt className="sample-record-panel__label" data-datum={datums.key} data-kind="label">
                {row.label}
              </dt>
              <dd className="sample-record-panel__value" data-datum={datums.value} data-kind="value">
                {row.value ?? '—'}
              </dd>
              {row.note && (
                <dd className="sample-record-panel__row-note" data-datum={datums.note} data-kind="hint">
                  {row.note}
                </dd>
              )}
            </div>
          ))}
        </dl>
      ) : (
        <p
          className="sample-record-panel__note"
          data-datum={datums.empty}
          data-kind="note"
          data-container={container}
        >
          {emptyText}
        </p>
      )}
      {/* `:1829-1836` — health is derived from what is composed. With nothing driving it,
          saying so is a statement about the type, not an empty slot. */}
      <p
        className="sample-record-panel__health"
        data-datum={datums.health}
        data-kind="callout"
        data-container={container}
      >
        {drivers.length > 0 ? (
          <>
            {t(`${KEY}.sampleHealthLabel`)}{' '}
            <strong data-datum={datums.onTrack} data-kind="strong">
              {t(`${KEY}.sampleHealthOnTrack`)}
            </strong>
            {' — '}
            {drivers.map((driver, index) => (
              <React.Fragment key={driver.key}>
                {index > 0 && ' + '}
                <span
                  className="sample-record-panel__driver"
                  data-datum={datums.driver}
                  data-kind="component"
                  data-container={`${container} > each(drivers)`}
                >
                  {driver.label} ({driver.weight})
                </span>
              </React.Fragment>
            ))}
            .
          </>
        ) : (
          t(`${KEY}.sampleNoHealth`)
        )}
      </p>
    </>
  );
};

SampleRecordBody.displayName = 'SampleRecordBody';

/**
 * The sandbox sphere the panel and the dialog can both stand up (`:1841`, `:2099`).
 * `unavailable` is not a disabled button with a hidden reason — every status the
 * action can be in is stated in text next to it.
 */
export interface SandboxSphereReadout {
  status: 'idle' | 'unavailable' | 'creating' | 'created' | 'deleting' | 'refused' | 'failed';
  sphereId?: string;
  name?: string;
}

const SANDBOX_MESSAGE_KEY: Record<SandboxSphereReadout['status'], string | null> = {
  idle: null,
  unavailable: 'sandboxUnavailable',
  creating: 'sandboxCreating',
  created: 'sandboxCreated',
  deleting: 'sandboxDeleting',
  refused: 'sandboxRefused',
  failed: 'sandboxFailed',
};

/** True while the action is in flight or has already produced a sphere. */
export const sandboxCreateDisabled = (sandbox?: SandboxSphereReadout): boolean =>
  sandbox !== undefined &&
  (sandbox.status === 'unavailable' ||
    sandbox.status === 'creating' ||
    sandbox.status === 'created' ||
    sandbox.status === 'deleting' ||
    sandbox.status === 'refused');

export interface SandboxSphereActionsProps {
  sandbox?: SandboxSphereReadout;
  onDeleteSandboxSphere?: () => void;
}

/**
 * What success looks like: the link to the sphere that now exists, and the delete
 * that undoes it. Rendered by the panel and the dialog alike, after each has
 * rendered its own contract-declared `[Create as sandbox sphere]` button.
 */
export const SandboxSphereActions: React.FC<SandboxSphereActionsProps> = ({
  sandbox,
  onDeleteSandboxSphere,
}) => {
  const { t } = useTranslation();
  if (!sandbox || sandbox.status === 'idle') return null;
  const messageKey = SANDBOX_MESSAGE_KEY[sandbox.status];
  return (
    <>
      {sandbox.status === 'created' && sandbox.sphereId && (
        <>
          <a
            className="sample-record-panel__sandbox-link"
            href={`/sphere/${encodeURIComponent(sandbox.sphereId)}`}
            data-testid="sample-record-sandbox-link"
          >
            {t(`${KEY}.sandboxOpen`, { name: sandbox.name ?? '' })}
          </a>
          <ButtonAtom
            variant="secondary"
            size="small"
            label={t(`${KEY}.sandboxDelete`)}
            onClick={onDeleteSandboxSphere}
            data-testid="sample-record-sandbox-delete"
          />
        </>
      )}
      {messageKey && (
        <p
          className="sample-record-panel__sandbox-note"
          role="status"
          data-testid="sample-record-sandbox-status"
        >
          {t(`${KEY}.${messageKey}`)}
        </p>
      )}
    </>
  );
};

SandboxSphereActions.displayName = 'SandboxSphereActions';

export interface SampleRecordPanelMoleculeProps {
  definition: Schema | null;
  /** The workspace registry the composed rows are read against. `null` = not read yet. */
  aspectCatalog?: AspectRow[] | null;
  /** `[Full sphere]` (`:1838`) — opens the same record in the wide dialog. */
  onOpenFullSphere?: () => void;
  /** `[Create as sandbox sphere]` (`:1841`). */
  onCreateSandboxSphere?: () => void;
  /** Removes the sandbox sphere the action created — success's undo. */
  onDeleteSandboxSphere?: () => void;
  /** Where the sandbox action has got to; absent leaves the button in its resting state. */
  sandbox?: SandboxSphereReadout;
}

/**
 * A synthetic, non-persisted view of what a real sphere of this type would hold —
 * "validate by example" (spec § Desired State).
 */
const SampleRecordPanelMolecule: React.FC<SampleRecordPanelMoleculeProps> = ({
  definition,
  aspectCatalog = null,
  onOpenFullSphere,
  onCreateSandboxSphere,
  onDeleteSandboxSphere,
  sandbox,
}) => {
  const { t } = useTranslation();
  const rows = React.useMemo(
    () => sampleRecordRows(definition, aspectCatalog, t),
    [definition, aspectCatalog, t]
  );
  const drivers = React.useMemo(() => sampleHealthDrivers(definition, t), [definition, t]);

  return (
    <div className="sample-record-panel" data-testid="sample-record-panel" data-container="side > panel">
      <h2
        className="sample-record-panel__title"
        data-datum="side.heading"
        data-kind="heading"
        data-container="side > panel"
      >
        <strong data-datum="side.samplenamefor-t" data-kind="strong">
          {sampleRecordName(definition, t)}
        </strong>
        <span className="sample-record-panel__qualifier" data-datum="side.t-name" data-kind="pill">
          {definition?.name ?? ''}
        </span>
      </h2>
      {/*
        `:1814`. The panel is a preview, not a record: saying so once here is what stops
        someone treating it as data they created.
      */}
      <p
        className="sample-record-panel__note"
        data-datum="side.live-it-follows-every-change-you-make-no"
        data-kind="hint"
        data-container="side > panel"
      >
        {t(`${KEY}.sampleLive`)}
      </p>
      <SampleRecordBody
        rows={rows}
        drivers={drivers}
        datums={PANEL_DATUMS}
        container="side > panel"
        emptyText={t(`${KEY}.sampleEmpty`)}
      />
      <div className="sample-record-panel__actions" data-container="side > panel > actions">
        <ButtonAtom
          variant="secondary"
          size="small"
          label={t(`${KEY}.fullSphere`)}
          onClick={onOpenFullSphere}
          data-datum="side.full-sphere"
          data-kind="button"
          data-testid="sample-record-full-sphere"
        />
        <ButtonAtom
          variant="secondary"
          size="small"
          label={t(`${KEY}.createSandboxSphere`)}
          onClick={onCreateSandboxSphere}
          disabled={sandboxCreateDisabled(sandbox)}
          data-datum="side.create-as-sandbox-sphere"
          data-kind="button"
          data-testid="sample-record-create-sandbox"
        />
        <SandboxSphereActions sandbox={sandbox} onDeleteSandboxSphere={onDeleteSandboxSphere} />
      </div>
    </div>
  );
};

SampleRecordPanelMolecule.displayName = 'SampleRecordPanelMolecule';

export default SampleRecordPanelMolecule;
